import React, { useState, useEffect } from 'react';
import { X, User, Phone, Mail, Calendar, Clock, AlertCircle, Stethoscope, Building } from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';
import { patientAPI } from '../services/api';

const PatientDetailsPopup = ({ patient, onClose }) => {
  const [diagnosisHistory, setDiagnosisHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDiagnosisHistory = async () => {
      try {
        if (patient._id) {
          const response = await patientAPI.getDiagnosisHistory(patient._id);
          setDiagnosisHistory(response);
        }
      } catch (error) {
        console.error('Error fetching diagnosis history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDiagnosisHistory();
  }, [patient._id]);

  if (!patient) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatProfileCompletion = (percentage) => {
    return `${percentage || 0}%`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl w-full max-w-2xl mx-4 relative max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header with Profile Image */}
        <div className="bg-gray-50 px-8 pt-8 pb-6 rounded-t-2xl border-b">
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center overflow-hidden mb-4">
              {patient.user?.profileImage ? (
                <img
                  src={getImageUrl(patient.user.profileImage)}
                  alt={patient.user.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-12 h-12 text-gray-400" />
              )}
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">{patient.user?.name}</h2>
            <p className="text-gray-600 mt-1">{patient.user?.email}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="space-y-8">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-gray-900">{patient.phone || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-500">Date of Birth</p>
                    <p className="text-gray-900">{formatDate(patient.dateOfBirth)}</p>
                  </div>
                </div>
              </div>
            </div>
            {/* GP Information */}
            {!loading && diagnosisHistory && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">GP Information</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  {/* Row 1: GP Name + Clinic Name */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start space-x-3">
                      <Stethoscope className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">GP Name</p>
                        <p className="text-gray-900">{diagnosisHistory.gp_name || 'Not provided'}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Building className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">Clinic Name</p>
                        <p className="text-gray-900">{diagnosisHistory.gp_clinic || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: GP Phone + Last Visit */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start space-x-3">
                      <Phone className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">GP Phone</p>
                        <p className="text-gray-900">{diagnosisHistory.gp_phone || 'Not provided'}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <Clock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                      <div>
                        <p className="text-sm text-gray-500">Last Visit</p>
                        <p className="text-gray-900">{formatDate(diagnosisHistory.last_visit_date)}</p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            )}


            {/* Care Plan Details */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Care Plan Details</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Profile Completion</p>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-blue-600 h-2.5 rounded-full" 
                      style={{ width: formatProfileCompletion(patient.profile_completion) }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{formatProfileCompletion(patient.profile_completion)} completed</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-2">Care Plan Status</p>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    patient.care_plan_onboarding_completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {patient.care_plan_onboarding_completed ? 'Completed' : 'Pending'}
                  </span>
                </div>

                {/* <div>
                  <p className="text-sm text-gray-500 mb-2">Subscription Status</p>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                    patient.subscription_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {patient.subscription_status || 'Inactive'}
                  </span>
                </div> */}

              

               
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetailsPopup; 